import { Injectable } from '@angular/core';
import { HttpClient, HttpEvent, HttpEventType, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { Document } from '../../models/document.model';
import { Dossier } from '../../models/dossier.model';

export interface DocumentSearchParams {
  fermeId: number;
  dossierId?: number;
  type?: string;
  search?: string;
  lienType?: string;
  lienId?: number;
  tag?: string;
  /** ACTIF (défaut) | CORBEILLE */
  statut?: string;
  /** Photos : EXCLURE (Pièces) | INCLURE | SEULES ; le serveur les exclut par défaut */
  photos?: 'EXCLURE' | 'INCLURE' | 'SEULES';
}

/** Métadonnées envoyées avec un fichier téléversé */
export interface MetaTeleversement {
  nom?: string;
  type?: string;
  dossierId?: number | null;
  /** Dossier système : FACTURES | RECUS | JUSTIFICATIFS | CONTRATS | RAPPORTS */
  dossierCode?: string;
  lienType?: string;
  lienId?: number;
  lienLabel?: string;
  tags?: string[];
  description?: string;
  origine?: 'UPLOAD' | 'PHOTO';
  dateDocument?: string;
  dateExpiration?: string;
  /** Date de prise d'une photo (ISO, heure locale) */
  datePrise?: string;
}

/** Progression d'un téléversement : pourcentage puis document créé */
export interface ProgressionTeleversement {
  pourcentage: number;
  document?: Document;
}

/** 2026-10-18T09:12:00, heure locale (sans zone) */
export function dateLocaleIso(ms: number): string {
  const d = new Date(ms);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

@Injectable({ providedIn: 'root' })
export class GedService {

  private readonly docsUrl = `${environment.BASE_URL}/api/documents`;
  private readonly dossUrl = `${environment.BASE_URL}/api/dossiers`;

  constructor(private http: HttpClient) {}

  // ── Documents ──────────────────────────────────────────────────────────────

  getDocuments(params: DocumentSearchParams): Observable<Document[]> {
    let p = new HttpParams().set('fermeId', params.fermeId);
    (['dossierId', 'type', 'search', 'lienType', 'lienId', 'tag', 'statut', 'photos'] as const).forEach(k => {
      const v = (params as any)[k];
      if (v != null && v !== '') p = p.set(k, v);
    });
    return this.http.get<Document[]>(this.docsUrl, { params: p });
  }

  /** Documents reliés à un objet métier (facture, dépense, contact, lot…) */
  getDocumentsLies(fermeId: number, lienType: string, lienId: number): Observable<Document[]> {
    return this.getDocuments({ fermeId, lienType, lienId });
  }

  getDocumentById(id: number): Observable<Document> {
    return this.http.get<Document>(`${this.docsUrl}/${id}`);
  }

  createDocument(dto: Document): Observable<Document> {
    return this.http.post<Document>(this.docsUrl, dto);
  }

  /** Téléversement par l'API (stockage isolé par ferme, vignette, lien) avec progression réelle */
  televerser(fermeId: number, fichier: Blob, nomFichier: string, meta: MetaTeleversement): Observable<ProgressionTeleversement> {
    const fd = new FormData();
    fd.append('fichier', fichier, nomFichier);
    // Photo : date de prise connue = date du fichier (lastModified), à défaut de mieux
    const f = fichier as File;
    if (meta && !meta.datePrise && f.lastModified && (f.type || '').startsWith('image/')) { meta = { ...meta, datePrise: dateLocaleIso(f.lastModified) }; }
    Object.entries(meta || {}).forEach(([k, v]) => {
      if (v == null || v === '') return;
      fd.append(k, Array.isArray(v) ? v.join(',') : String(v));
    });
    return this.http.post<Document>(`${this.docsUrl}/upload`, fd, {
      params: { fermeId }, reportProgress: true, observe: 'events'
    }).pipe(
      filter((e: HttpEvent<Document>) => e.type === HttpEventType.UploadProgress || e.type === HttpEventType.Response),
      map((e: any) => e.type === HttpEventType.Response
        ? { pourcentage: 100, document: e.body as Document }
        : { pourcentage: e.total ? Math.min(99, Math.round(100 * e.loaded / e.total)) : 50 })
    );
  }

  /** Rattache un document existant (photo de la Galerie) à une fiche, sans renvoyer le fichier (idempotent) */
  lier(id: number, lien: { lienType: string; lienId: number; lienLabel?: string }): Observable<Document> {
    return this.http.post<Document>(`${this.docsUrl}/${id}/liens`, lien);
  }

  updateDocument(id: number, dto: Partial<Document>): Observable<Document> {
    return this.http.put<Document>(`${this.docsUrl}/${id}`, dto);
  }

  /** Mise à la corbeille */
  deleteDocument(id: number): Observable<void> {
    return this.http.delete<void>(`${this.docsUrl}/${id}`);
  }

  restaurer(id: number): Observable<Document> {
    return this.http.post<Document>(`${this.docsUrl}/${id}/restaurer`, {});
  }

  purger(fermeId: number, force = false): Observable<{ purges: number }> {
    return this.http.post<{ purges: number }>(`${this.docsUrl}/purge`, {}, { params: { fermeId, force } });
  }

  stats(fermeId: number): Observable<StatsDocuments> {
    return this.http.get<StatsDocuments>(`${this.docsUrl}/stats`, { params: { fermeId } });
  }

  lienPublic(id: number): Observable<{ url: string }> {
    return this.http.post<{ url: string }>(`${this.docsUrl}/${id}/lien-public`, {});
  }

  revoquerLien(id: number): Observable<void> {
    return this.http.delete<void>(`${this.docsUrl}/${id}/lien-public`);
  }

  // ── Fichiers (authentifiés : récupérés en blob, jamais d'URL publique) ────

  fichier(doc: Document): Observable<Blob> {
    return this.http.get(`${this.docsUrl}/${doc.id}/fichier`, { responseType: 'blob' });
  }

  miniature(id: number): Observable<Blob> {
    return this.http.get(`${this.docsUrl}/${id}/miniature`, { responseType: 'blob' });
  }

  /** Ouvre le document dans un nouvel onglet (fenêtre ouverte avant l'appel : pas de blocage de popup) */
  ouvrir(doc: Document): void {
    if (!doc.fichierDisponible && doc.fileUrl) {
      window.open(doc.fileUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    const fenetre = window.open('', '_blank');
    if (fenetre) { fenetre.document.title = doc.nom; fenetre.document.body.innerText = 'Chargement du document…'; }
    this.fichier(doc).subscribe({
      next: blob => {
        const url = URL.createObjectURL(blob);
        if (fenetre) { fenetre.location.href = url; } else { window.location.href = url; }
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      },
      error: () => { fenetre?.close(); }
    });
  }

  telecharger(doc: Document): void {
    const obs: Observable<Blob> = !doc.fichierDisponible && doc.fileUrl
      ? this.http.get(doc.fileUrl, { responseType: 'blob' })
      : this.fichier(doc);
    obs.subscribe(blob => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = nomFichierDocument(doc);
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    });
  }

  // ── Dossiers ───────────────────────────────────────────────────────────────

  getDossiers(fermeId: number): Observable<Dossier[]> {
    return this.http.get<Dossier[]>(this.dossUrl, { params: { fermeId } });
  }

  getDossier(id: number): Observable<Dossier> {
    return this.http.get<Dossier>(`${this.dossUrl}/${id}`);
  }

  createDossier(dto: Dossier): Observable<Dossier> {
    return this.http.post<Dossier>(this.dossUrl, dto);
  }

  updateDossier(id: number, dto: Partial<Dossier>): Observable<Dossier> {
    return this.http.put<Dossier>(`${this.dossUrl}/${id}`, dto);
  }

  deleteDossier(id: number): Observable<void> {
    return this.http.delete<void>(`${this.dossUrl}/${id}`);
  }
}

export interface StatsDocuments {
  total: number;
  tailleTotale: number;
  corbeille: number;
  parType: { [type: string]: number };
  tags: { [tag: string]: number };
  expirant: { id: number; nom: string; dateExpiration: string; jours: number }[];
}

export function nomFichierDocument(doc: Document): string {
  const ext = doc.mimeType === 'application/pdf' ? '.pdf'
    : doc.mimeType?.startsWith('image/') ? '.' + doc.mimeType.slice(6).replace('jpeg', 'jpg') : '';
  const base = (doc.nom || 'document').replace(/[\\/:*?"<>|]/g, '_');
  return base.toLowerCase().endsWith(ext) ? base : base + ext;
}
